import React from 'react';
import { ComponentLibraryLoader } from '@unite-us/app-components';
import { datadogRum } from '@datadog/browser-rum';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Notifier from 'common/helpers/Notifier';
import { browserHistory } from 'common/utils/browserHistory';
import { hint2552ShowNewProviderAdmin as hint2552ShowNewProviderAdminSelector } from 'common/utils/FeatureFlags/flags';
import { apiV1, authApi, searchAPIClient } from 'src/api/config';
import useTrackEvent from 'src/components/Organization/utils/useTrackEvent';
import fetchMyProviders from 'src/actions/Login/fetchMyProviders';
import setCurrentProvider from 'src/components/App/actions/setCurrentProvider';
import openProvider from 'src/actions/Agreement/openProvider';
import { updateGlobalState } from 'src/actions/Global/globalActions';
import { env } from 'src/config/env';
import './ProviderAdmin.scss';

const AppPA = ComponentLibraryLoader({
  loadFn: () => import('@unite-us/app-provider-admin'),
  component: 'AppProviderAdmin',
  loadingApp: 'app-client',
  source: 'app-provider-admin',
  datadogRum,
});

const getBasename = ({ pathname, route, basename = '' }) => {
  const path = pathname.split(route).at(0);
  return `${basename}${path}${route}`;
};

const ProviderAdmin = ({
  hint2552ShowNewProviderAdmin,
  currentProviderId,
  globalEmployees,
  updateGlobalStateAction,
  fetchMyProvidersAction,
  setCurrentProviderAction,
  openProviderAction,
}) => {
  if (!hint2552ShowNewProviderAdmin) {
    browserHistory.push('/');
    setTimeout(() => {
      // eslint-disable-next-line no-restricted-globals
      location.reload();
    }, []);
    return null;
  }

  const refreshProvider = async (updatedProvider) => {
    const providerId = updatedProvider.id;
    const refreshedEmployees = globalEmployees.map((employee) => {
      if (employee.provider?.id !== providerId) {
        return employee;
      }
      return {
        ...employee,
        provider: {
          ...employee.provider,
          ...(updatedProvider || {}),
          state: 'active',
        },
      };
    });

    updateGlobalStateAction({ employees: refreshedEmployees });
    await fetchMyProvidersAction({ employees: refreshedEmployees });
    setCurrentProviderAction(providerId);
    openProviderAction(providerId);
  };

  const basename = getBasename({
    // eslint-disable-next-line no-restricted-globals
    basename: location.basename,
    // eslint-disable-next-line no-restricted-globals
    pathname: location.pathname,
    route: 'provider-admin',
  });

  return (
    <AppPA
      adapters={{
        apiV1,
        authApi,
        searchApi: searchAPIClient,
      }}
      appSettings={{
        endpoints: {
          providerOnboarding: {
            url: env.PROVIDER_ONBOARDING_API,
          },
        },
        providerId: currentProviderId,
      }}
      basename={basename}
      callbacks={{
        notify: {
          error: (message) => Notifier.dispatch('error', message),
          success: (message) => {
            Notifier.dispatch('success', message);
          },
          warn: (message) => Notifier.dispatch('warning', message),
        },
        useTrackEvent,
        refreshProvider,
      }}
    />
  );
};

ProviderAdmin.propTypes = {
  currentProviderId: PropTypes.string.isRequired,
  hint2552ShowNewProviderAdmin: PropTypes.bool,
  globalEmployees: PropTypes.array.isRequired,
  updateGlobalStateAction: PropTypes.func.isRequired,
  fetchMyProvidersAction: PropTypes.func.isRequired,
  setCurrentProviderAction: PropTypes.func.isRequired,
  openProviderAction: PropTypes.func.isRequired,
};

ProviderAdmin.defaultProps = {
  hint2552ShowNewProviderAdmin: false,
};

function mapStateToProps(state) {
  const globalEmployees = state.globalState?.employees || [];
  const currentProviderId = state.session.groupId;
  const hint2552ShowNewProviderAdmin = hint2552ShowNewProviderAdminSelector(state);
  return {
    currentProviderId,
    hint2552ShowNewProviderAdmin,
    globalEmployees,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    fetchMyProvidersAction: (obj) => dispatch(fetchMyProviders(obj)),
    setCurrentProviderAction: (provider) => dispatch(setCurrentProvider(provider)),
    openProviderAction: (provider) => dispatch(openProvider(provider)),
    updateGlobalStateAction: (payload) => dispatch(updateGlobalState(payload)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ProviderAdmin);
